/**
 * Zustand store wrapping the engine reducer with autoresume + HH persistence.
 *
 * Lifecycle:
 *   startTournament() → initTournament + first startHand → status='awaiting-action'
 *   applyAction(action) → reducer.step → autosave snapshot →
 *     if hand ends: write HandRow, applyHandEnd → status='between-hands' OR 'finished'
 *     else:         status='awaiting-action'
 *   advanceHand() → next startHand
 *
 * Inject `services.db` and `services.evaluator` for tests; production falls
 * back to the singleton db and pokersolver evaluator.
 */
import { create } from 'zustand';
import { createPokersolverEvaluator } from '@/engine/handEvaluator';
import type { HandEvaluator } from '@/engine/ports';
import { type ReducerServices, startHand, step } from '@/engine/reducer';
import { createRng } from '@/engine/rng';
import {
  applyHandEnd,
  applyRebuy,
  canPlayerRebuy,
  computeFinalStandings,
  getBlindsForHand,
  initTournament,
  resetPlayersForHand,
} from '@/engine/tournament';
import type { Action, Card, Event, HandState, PlayerId, TournamentState } from '@/engine/types';
import { getDb, type HandPayout, type PokerDb } from '@/storage/db';
import { rowFromRecord, saveHand, saveTournament, updateTournament } from '@/storage/handHistory';
import { recordFromEngine, serializeHand } from '@/storage/pokerstars';
import { clearResumeSnapshot, loadResumeSnapshot, saveResumeSnapshot } from '@/storage/resume';

export type LastHandSummary = {
  handNumber: number;
  board: ReadonlyArray<Card>;
  payouts: ReadonlyArray<HandPayout>;
  holeCardsByPlayer: Readonly<Record<PlayerId, readonly [Card, Card]>>;
  stackDeltaByPlayer: Readonly<Record<PlayerId, number>>;
  /** PlayerIds that took at least one pot. */
  winnerIds: ReadonlyArray<PlayerId>;
  /** PlayerIds who folded during this hand — real-poker-correct muck: their
   *  hole cards should NOT be revealed during between-hands even though the
   *  raw `holeCardsByPlayer` map still contains them for HH export/review. */
  foldedIds: ReadonlyArray<PlayerId>;
  /** The 5-card best hand for the main-pot winner, if showdown reached. */
  winningFive: ReadonlyArray<Card>;
  /** Human-readable name for the winning hand (e.g. "Two Pair, A's & K's"). */
  winningDescription?: string;
};

export type GameStatus = 'idle' | 'awaiting-action' | 'between-hands' | 'finished';

export type StartTournamentOptions = {
  tournamentId: string;
  seed: string;
  playerIds: ReadonlyArray<PlayerId>;
  heroId: PlayerId;
  characterCodenames?: ReadonlyArray<string>;
  agentMode?: 'offline' | 'llm';
};

export type GameStoreServices = {
  db?: PokerDb;
  evaluator?: HandEvaluator;
  /** Override `Date.now()` for deterministic timestamps in tests. */
  now?: () => number;
};

export type GameStoreState = {
  tournament: TournamentState | null;
  hand: HandState | null;
  /** Snapshot of `hand` immediately after startHand — needed at hand-end to
   *  build the canonical HandRecord (the live `hand` is mid-hand by then). */
  initialHand: HandState | null;
  events: ReadonlyArray<Event>;
  startOfHandStacks: Readonly<Record<PlayerId, number>>;
  heroId: PlayerId | null;
  tournamentId: string | null;
  seed: string | null;
  status: GameStatus;
  services: GameStoreServices;
  reducerServices: ReducerServices | null;
  /** Snapshot of the just-finished hand used by the HandEndOverlay.
   *  Cleared when `advanceHand` starts the next hand or on reset. */
  lastHandSummary: LastHandSummary | null;

  configure(services: GameStoreServices): void;
  startTournament(opts: StartTournamentOptions): Promise<void>;
  applyAction(action: Action): Promise<void>;
  advanceHand(): Promise<void>;
  loadResume(tournamentId: string): Promise<boolean>;
  reset(): void;
};

const initialState = {
  tournament: null,
  hand: null,
  initialHand: null,
  events: [] as ReadonlyArray<Event>,
  startOfHandStacks: {} as Readonly<Record<PlayerId, number>>,
  heroId: null,
  tournamentId: null,
  seed: null,
  status: 'idle' as GameStatus,
  reducerServices: null,
  lastHandSummary: null as LastHandSummary | null,
} as const;

function resolveDb(services: GameStoreServices): PokerDb {
  return services.db ?? getDb();
}

function resolveEvaluator(services: GameStoreServices): HandEvaluator {
  return services.evaluator ?? createPokersolverEvaluator();
}

function resolveNow(services: GameStoreServices): number {
  return services.now ? services.now() : Date.now();
}

function preparePreHand(tournament: TournamentState): TournamentState {
  if (tournament.rebuysLocked) return tournament;
  const nextHand = tournament.handNumber + 1;
  return {
    ...tournament,
    players: tournament.players.map((p) => (canPlayerRebuy(p, nextHand) ? applyRebuy(p) : p)),
  };
}

function dealNextHand(
  tournament: TournamentState,
  seed: string,
): {
  initial: HandState;
  events: Event[];
  startOfHandStacks: Record<PlayerId, number>;
} {
  const nextHandNumber = tournament.handNumber + 1;
  const reset = resetPlayersForHand(tournament.players);
  const startOfHandStacks: Record<PlayerId, number> = {};
  for (const p of reset) startOfHandStacks[p.id] = p.stack;
  const { state: initial, events } = startHand({
    handNumber: nextHandNumber,
    level: getBlindsForHand(nextHandNumber),
    seed: `${seed}:${nextHandNumber}`,
    button: tournament.button,
    players: reset,
    rng: createRng(`${seed}:${nextHandNumber}`),
  });
  return { initial, events, startOfHandStacks };
}

function heroRank(tournament: TournamentState, heroId: PlayerId): number | null {
  if (!tournament.finished) return null;
  try {
    const standings = computeFinalStandings(tournament);
    return standings.find((row) => row.playerId === heroId)?.rank ?? null;
  } catch {
    return null;
  }
}

export const useGameStore = create<GameStoreState>((set, get) => ({
  ...initialState,
  services: {},

  configure(services) {
    set({ services });
  },

  async startTournament(opts) {
    const { tournamentId, seed, playerIds, heroId, characterCodenames, agentMode } = opts;
    const services = get().services;
    const evaluator = resolveEvaluator(services);
    const reducerServices: ReducerServices = {
      rng: createRng(`${seed}-svc`),
      evaluator,
    };
    // Rebuys disabled at the UX layer: flag every seat as having already
    // used its rebuy so busted players elim immediately (applyHandEnd in
    // engine/tournament.ts uses `rebuyUsed` as an elim gate). Engine-level
    // rebuy helpers stay intact for sim/tests.
    const initialTournament = initTournament({ seed, playerIds });
    const baseTournament = preparePreHand({
      ...initialTournament,
      players: initialTournament.players.map((p) => ({ ...p, rebuyUsed: true })),
    });
    const now = resolveNow(services);
    await saveTournament(
      {
        id: tournamentId,
        startedAt: now,
        endedAt: null,
        resultRank: null,
        prizePoints: baseTournament.prizePool,
        meta: {
          playerIds: [...playerIds],
          characterCodenames: characterCodenames ? [...characterCodenames] : undefined,
          agentMode: agentMode ?? 'offline',
        },
      },
      resolveDb(services),
    );
    const { initial, events, startOfHandStacks } = dealNextHand(baseTournament, seed);
    set({
      tournament: baseTournament,
      hand: initial,
      initialHand: initial,
      events,
      startOfHandStacks,
      heroId,
      tournamentId,
      seed,
      status: 'awaiting-action',
      reducerServices,
    });
    await saveResumeSnapshot(
      tournamentId,
      { tournamentState: baseTournament, handState: initial, events },
      resolveDb(services),
    );
  },

  async applyAction(action) {
    const s = get();
    if (
      !s.tournament ||
      !s.hand ||
      !s.initialHand ||
      !s.reducerServices ||
      !s.tournamentId ||
      !s.heroId ||
      !s.seed
    ) {
      throw new Error('gameStore.applyAction: not in a hand');
    }
    const result = step(s.hand, action, s.reducerServices);
    const nextHand = result.state;
    const nextEvents = [...s.events, ...result.events];
    const db = resolveDb(s.services);

    if (nextHand.pendingActor === null && nextHand.results) {
      const playedAt = resolveNow(s.services);
      const record = recordFromEngine({
        tournamentId: s.tournamentId,
        initialState: s.initialHand,
        finalState: nextHand,
        events: nextEvents,
        startOfHandStacks: s.startOfHandStacks,
        heroId: s.heroId,
        playedAt,
      });
      await saveHand(rowFromRecord(record, serializeHand(record)), db);

      const advanced = applyHandEnd(s.tournament, nextHand.players, s.startOfHandStacks);
      const finished = advanced.finished;
      if (finished) {
        await updateTournament(
          s.tournamentId,
          {
            endedAt: playedAt,
            prizePoints: advanced.prizePool,
            resultRank: heroRank(advanced, s.heroId),
          },
          db,
        );
        await clearResumeSnapshot(s.tournamentId, db);
      } else {
        await saveResumeSnapshot(
          s.tournamentId,
          { tournamentState: advanced, handState: null, events: [] },
          db,
        );
      }
      const holeCardsByPlayer: Record<PlayerId, readonly [Card, Card]> = {};
      for (const p of nextHand.players) {
        if (p.holeCards) holeCardsByPlayer[p.id] = p.holeCards;
      }
      const stackDeltaByPlayer: Record<PlayerId, number> = {};
      for (const p of nextHand.players) {
        const startStack = s.startOfHandStacks[p.id] ?? p.stack;
        stackDeltaByPlayer[p.id] = p.stack - startStack;
      }
      const payouts = nextHand.results?.payouts ?? [];
      // Unique winner IDs preserving first-appearance order (main pot first).
      const winnerIds: PlayerId[] = [];
      for (const p of payouts) {
        if (!winnerIds.includes(p.playerId)) winnerIds.push(p.playerId);
      }
      // Compute the winning 5-card combination for the top payout when a
      // showdown reached the river and the winner's hole cards are known.
      let winningFive: Card[] = [];
      let winningDescription: string | undefined;
      const topPayout = payouts[0];
      if (topPayout) {
        const winnerHole = holeCardsByPlayer[topPayout.playerId];
        if (winnerHole && nextHand.board.length === 5) {
          try {
            const rank = s.reducerServices.evaluator.rank([...winnerHole, ...nextHand.board]);
            winningFive = [...rank.best];
            winningDescription = topPayout.handDescription ?? rank.description;
          } catch {
            // ignore — leave empty
          }
        } else if (topPayout.handDescription) {
          winningDescription = topPayout.handDescription;
        }
      }
      const foldedIds: PlayerId[] = nextHand.players.filter((p) => p.folded).map((p) => p.id);
      const summary: LastHandSummary = {
        handNumber: nextHand.handNumber,
        board: nextHand.board,
        payouts,
        holeCardsByPlayer,
        stackDeltaByPlayer,
        winnerIds,
        foldedIds,
        winningFive,
        winningDescription,
      };
      set({
        tournament: advanced,
        hand: null,
        initialHand: null,
        events: [],
        startOfHandStacks: {},
        status: finished ? 'finished' : 'between-hands',
        lastHandSummary: summary,
      });
      return;
    }

    set({ hand: nextHand, events: nextEvents, status: 'awaiting-action' });
    // Fire-and-forget the mid-hand resume snapshot. Dexie writes take
    // ~5-50ms in browsers; awaiting them here makes the UI wait on
    // IndexedDB between every single action. A dropped autosave is
    // recoverable (the next action rewrites it); blocking play is not.
    void saveResumeSnapshot(
      s.tournamentId,
      { tournamentState: s.tournament, handState: nextHand, events: nextEvents },
      db,
    ).catch((err) => {
      console.error('saveResumeSnapshot failed', err);
    });
  },

  async advanceHand() {
    const s = get();
    if (!s.tournament || !s.reducerServices || !s.tournamentId || !s.seed) {
      throw new Error('gameStore.advanceHand: not in a tournament');
    }
    if (s.status !== 'between-hands') {
      throw new Error(`gameStore.advanceHand: status is ${s.status}, expected 'between-hands'`);
    }
    const next = preparePreHand(s.tournament);
    if (next.players.filter((p) => !p.eliminated && p.stack > 0).length < 2) {
      set({ tournament: next, status: 'finished' });
      return;
    }
    const { initial, events, startOfHandStacks } = dealNextHand(next, s.seed);
    set({
      tournament: next,
      hand: initial,
      initialHand: initial,
      events,
      startOfHandStacks,
      status: 'awaiting-action',
      lastHandSummary: null,
    });
    await saveResumeSnapshot(
      s.tournamentId,
      { tournamentState: next, handState: initial, events },
      resolveDb(s.services),
    );
  },

  async loadResume(tournamentId) {
    const services = get().services;
    const snap = await loadResumeSnapshot(tournamentId, resolveDb(services));
    if (!snap) return false;
    const evaluator = resolveEvaluator(services);
    const reducerServices: ReducerServices = {
      rng: createRng(`${snap.tournamentState.seed}-svc`),
      evaluator,
    };
    const startOfHandStacks: Record<PlayerId, number> = {};
    if (snap.handState) {
      for (const p of snap.handState.players) {
        // Stack at start-of-hand = current stack + chips already committed.
        startOfHandStacks[p.id] = p.stack + p.handContribution;
      }
    }
    set({
      tournament: snap.tournamentState,
      hand: snap.handState,
      initialHand: snap.handState,
      events: snap.events,
      startOfHandStacks,
      tournamentId,
      seed: snap.tournamentState.seed,
      status: snap.handState ? 'awaiting-action' : 'between-hands',
      reducerServices,
    });
    return true;
  },

  reset() {
    set({ ...initialState, services: {} });
  },
}));
